import React from "react";
import {
    alpha, Box, Button, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    IconButton, LinearProgress, List, ListItem, ListItemText, Paper, Popover, Stack, TextField,
    Tooltip, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {DataGrid} from "@mui/x-data-grid";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BuildIcon from "@mui/icons-material/BuildCircle";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ArticleIcon from "@mui/icons-material/Article";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {useTranslation} from "react-i18next";
import ApiAnalyticsEngineCtrl from "../../../../api/ApiAnalyticsEngineCtrl";
import {groupColor} from "../../../../utils/groupColor";
import {dgStyle} from "../utils/analyticsUtils";
import ModelEditDialog from "../catalog/AnalyticsModelEditDialog";
import BuildLogsViewer from "../catalog/BuildLogsViewer";
import { useToast } from "../../../../context/ToastContext";
import {useAutoRefresh} from "../../../../hooks/useAutoRefresh";
import RefreshControls from "../../../../components/RefreshControls";

// ── Helpers ────────────────────────────────────────────────────────────────────

function buildStatusColor(status) {
    if (!status) return "default";
    const s = status.toUpperCase();
    if (["SUCCESS", "COMPLETED", "OK"].includes(s))          return "success";
    if (["FAILED", "ERROR", "FAILURE"].includes(s))           return "error";
    if (["RUNNING", "BUILDING", "IN_PROGRESS"].includes(s))   return "info";
    if (["PENDING", "QUEUED"].includes(s))                    return "warning";
    return "default";
}

function extractStatus(data) {
    if (!data) return null;
    if (typeof data === "string") return data;
    return data.status ?? data.STATUS ?? null;
}

/** Compare deux versions semver-like — retourne >0 si a plus récent que b. */
function cmpVersion(a, b) {
    const pa = String(a || "0").split(".").map(n => parseInt(n, 10) || 0);
    const pb = String(b || "0").split(".").map(n => parseInt(n, 10) || 0);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const d = (pa[i] || 0) - (pb[i] || 0);
        if (d !== 0) return d;
    }
    return 0;
}

/** Regroupe les modèles par id logique — un item par id, versions triées desc. */
function groupByModelId(models) {
    const map = new Map();
    for (const m of (models || [])) {
        if (!map.has(m.id)) map.set(m.id, []);
        map.get(m.id).push(m);
    }
    const out = [];
    for (const [, versions] of map) {
        versions.sort((a, b) => cmpVersion(b.version || b.image?.tag, a.version || a.image?.tag));
        const latest = versions[0];
        out.push({...latest, _versions: versions});
    }
    return out;
}

export default function PageAdminAnalyticsCatalog() {
    const { t } = useTranslation();
    const [models,        setModels]        = React.useState([]);
    const [loading,       setLoading]       = React.useState(true);
    const [editTarget,    setEditTarget]    = React.useState(null);
    const [buildLogsModelId, setBuildLogsModelId] = React.useState(null);
    const [bulkOpen,      setBulkOpen]       = React.useState(false);
    const [bulkJson,      setBulkJson]       = React.useState("");
    const [bulkError,     setBulkError]      = React.useState(null);
    const [bulkImporting, setBulkImporting]  = React.useState(false);
    const [bulkProgress,  setBulkProgress]   = React.useState({done: 0, total: 0, errors: []});
    const bulkCancelRef = React.useRef(false);

    // Build statuses
    const [buildStatuses,    setBuildStatuses]     = React.useState({}); // modelId → raw data
    const [buildStsLoading,  setBuildStsLoading]   = React.useState(false);
    // K8s deploy statuses — modelId → {replicas, ...}
    const [deployStatuses,   setDeployStatuses]    = React.useState({});

    const [building, setBuilding] = React.useState(null);
    // Menu deroulant "versions" d'un modele groupe
    const [versionsMenu, setVersionsMenu] = React.useState({anchor: null, row: null});

    // Versions groupees par id logique — un item par id, versions triees desc
    const groupedModels = React.useMemo(() => groupByModelId(models), [models]);

    const { showToast } = useToast();

    const load = React.useCallback(async () => {
        setLoading(true);
        const {data: mdl} = await ApiAnalyticsEngineCtrl.getEngineModels();
        if (mdl) setModels(mdl);
        setLoading(false);

        // Charger les statuts de build en arrière-plan (ne bloque pas la grille)
        if (mdl) {
            setBuildStsLoading(true);
            const entries = await Promise.all(mdl.map(async m => {
                const {data} = await ApiAnalyticsEngineCtrl.getBuildStatus(m.id);
                return [m.id, data];
            }));
            setBuildStatuses(Object.fromEntries(entries.filter(([, d]) => d != null)));
            setBuildStsLoading(false);

            // Charger les statuts de déploiement K8s
            const {data: statuses} = await ApiAnalyticsEngineCtrl.getAllEngineStatuses();
            if (statuses) {
                setDeployStatuses(Object.fromEntries(
                    statuses.filter(s => s.modelId).map(s => [s.modelId, s])
                ));
            }
        }
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const {autoRefresh, setAutoRefresh, intervalMs, setIntervalMs} = useAutoRefresh(load);

    const groups = React.useMemo(() => {
        const seen = new Map();
        models.forEach(m => {
            if (m.groupId && !seen.has(m.groupId))
                seen.set(m.groupId, {id: m.groupId, label: m.groupLabel ?? m.groupId});
        });
        return Array.from(seen.values());
    }, [models]);

    // ── Handlers ────────────────────────────────────────────────────────────────

    async function handleRebuild(model) {
        setBuilding(model.id + "@" + (model.version || ""));
        // Recharger le modèle complet (la liste peut ne pas avoir image.files).
        // Si une version specifique est fournie, utiliser l'endpoint versionne.
        let full = model;
        if (model.version) {
            const r = await ApiAnalyticsEngineCtrl.getEngineModelVersion?.(model.id, model.version);
            if (r?.data) full = r.data;
        } else {
            const r = await ApiAnalyticsEngineCtrl.getEngineModel(model.id);
            if (r?.data) full = r.data;
        }
        await ApiAnalyticsEngineCtrl.saveEngineModel(full);
        setBuilding(null);
        load();
    }

    async function handleDeleteVersion(modelId, version) {
        const fn = ApiAnalyticsEngineCtrl.deleteEngineModelVersion;
        if (fn) {
            await fn(modelId, version);
        } else {
            // Fallback : appel direct si pas encore regenere cote openapi
            await fetch(`/api/analytics/engines/models/${encodeURIComponent(modelId)}/versions/${encodeURIComponent(version)}`, {method: "DELETE"});
        }
        showToast({msg: t('admin.catalog.toast.deleted', {id: `${modelId}@${version}`}), severity: "success"});
        setVersionsMenu({anchor: null, row: null});
        load();
    }

    async function handleDelete(id) {
        // Vérifier si le modèle a un déploiement K8s actif
        const {data: status} = await ApiAnalyticsEngineCtrl.getEngineStatus(id);
        if (status && status.replicas > 0) {
            showToast({msg: t('admin.catalog.toast.deleteDeployedError', {id, replicas: status.replicas, s: status.replicas > 1 ? "s" : ""}), severity: "error"});
            return;
        }
        await ApiAnalyticsEngineCtrl.deleteEngineModel(id);
        showToast({msg: t('admin.catalog.toast.deleted', {id}), severity: "success"});
        load();
    }

    async function handleCopyJson(model) {
        try { await navigator.clipboard.writeText(JSON.stringify(model, null, 2)); } catch {}
    }

    function handleBulkOpen() {
        setBulkJson("");
        setBulkError(null);
        setBulkImporting(false);
        setBulkProgress({done: 0, total: 0, errors: []});
        bulkCancelRef.current = false;
        setBulkOpen(true);
    }

    /** Normalise un modèle pour l'API (même logique que ModelEditDialog.handleSave) */
    function normalizeModelForSave(model) {
        let img = model.image;
        // Convertir image string → objet
        if (typeof img === "string" && img) {
            const m = img.match(/^(.+?)\/(.+):(.+)$/);
            img = m ? {registry: m[1], image: m[2], tag: m[3]} : {registry: "", image: img, tag: ""};
        } else if (!img || typeof img !== "object") {
            img = {registry: "", image: "", tag: ""};
        } else {
            img = {...img};
        }
        // Sans code source pour le bulk
        delete img.files;
        delete img.language;
        if (!img.sourceMode) img.sourceMode = "NONE";
        if (model.version && !img.tag) img.tag = model.version;
        return {
            ...model,
            nbSeries: model.series?.length ?? model.nbSeries ?? 1,
            image: img,
        };
    }

    async function handleBulkImport() {
        let parsed;
        try {
            parsed = JSON.parse(bulkJson);
            if (!Array.isArray(parsed)) {
                setBulkError(t('admin.catalog.bulk.validationNotArray'));
                return;
            }
            if (parsed.length === 0) {
                setBulkError(t('admin.catalog.bulk.validationEmpty'));
                return;
            }
        } catch (e) {
            setBulkError(t('admin.catalog.bulk.validationInvalidJson', {message: e.message}));
            return;
        }

        setBulkError(null);
        setBulkImporting(true);
        bulkCancelRef.current = false;
        const total = parsed.length;
        const errors = [];
        let done = 0;
        setBulkProgress({done: 0, total, errors: []});

        for (const model of parsed) {
            if (bulkCancelRef.current) break;
            const payload = normalizeModelForSave(model);
            const {error} = await ApiAnalyticsEngineCtrl.saveEngineModel(payload);
            done++;
            if (error) {
                errors.push(model.id || `index ${done}`);
            }
            setBulkProgress({done, total, errors: [...errors]});
        }

        const cancelled = bulkCancelRef.current;
        setBulkImporting(false);

        if (cancelled) {
            showToast({msg: t('admin.catalog.toast.importCancelled', {done, total}), severity: "info"});
            load();
        } else if (errors.length === 0) {
            showToast({msg: t('admin.catalog.toast.importSuccess', {count: total, s: total > 1 ? "s" : ""}), severity: "success"});
            setBulkOpen(false);
            load();
        } else {
            showToast({msg: t('admin.catalog.toast.importPartial', {success: total - errors.length, total, errors: errors.length, s: errors.length > 1 ? "s" : ""}), severity: "warning"});
            load();
        }
    }

    function handleClone(model) {
        // L'id logique reste identique (clustering-kmeans, stats-pearsonr...).
        // Seule la version change — la cle composite backend est (id, version).
        const existingVersions = models
            .filter(m => m.id === model.id)
            .map(m => m.version || (typeof m.image === "object" ? m.image?.tag : "") || "0.0.0");
        let maxParts = [0, 0, 0];
        existingVersions.forEach(v => {
            const p = v.split(".").map(Number);
            for (let i = 0; i < 3; i++) {
                if ((p[i] || 0) > maxParts[i]) { maxParts = p.slice(0, 3); return; }
                if ((p[i] || 0) < maxParts[i]) return;
            }
        });
        maxParts[2] = (maxParts[2] || 0) + 1;
        const nextVersion = maxParts.join(".");
        const v = prompt(t('admin.catalog.clone.prompt', {id: model.id}), nextVersion);
        if (!v) return;
        if (existingVersions.includes(v)) {
            alert(t('admin.catalog.clone.versionExists', {version: v, id: model.id}) || `La version ${v} existe déjà pour ${model.id}`);
            return;
        }
        const {_rowId, ...data} = model;
        const img = typeof data.image === "object" && data.image ? {...data.image, tag: v} : {tag: v};
        setEditTarget({...data, id: model.id, version: v, image: img, _isClone: true});
    }

    // ── Colonnes ────────────────────────────────────────────────────────────────

    const columns = [
        {
            field: "groupId", headerName: t('admin.catalog.col.group'), width: 180,
            align: "left", headerAlign: "left",
            renderCell: ({row}) => {
                const label = row.groupLabel || row.groupId;
                if (!label) return (
                    <Typography variant="caption" sx={{
                        color: "text.disabled"
                    }}>—</Typography>
                );
                const gc = groupColor(row.groupId || "default");
                return (
                    <Chip label={label} size="small"
                          sx={{
                              color: gc,
                              backgroundColor: alpha(gc, 0.10),
                              border: `1.5px solid ${alpha(gc, 0.5)}`,
                              fontWeight: 700,
                              letterSpacing: 0.3,
                          }}/>
                );
            },
        },
        {
            field: "id", headerName: t('admin.catalog.col.model'), flex: 2, minWidth: 200,
            align: "left", headerAlign: "left",
            renderCell: ({row}) => (
                <Stack
                    sx={{
                        justifyContent: "center",
                        height: "100%"
                    }}>
                    <Typography variant="body2" noWrap title={row.id} sx={{
                        fontWeight: 700
                    }}>{row.id}</Typography>
                </Stack>
            ),
        },
        {
            field: "version", headerName: t('admin.catalog.col.version'), width: 120,
            align: "center", headerAlign: "center",
            renderCell: ({row}) => {
                const v = row.version || (typeof row.image === "object" ? row.image?.tag : "") || "";
                const nbVersions = row._versions?.length || 1;
                if (!v) return "—";
                if (nbVersions <= 1) {
                    return <Chip label={`v${v}`} size="small" variant="outlined" sx={{height: 20, fontSize: 10}}/>;
                }
                return (
                    <Chip
                        label={`v${v} +${nbVersions - 1}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        deleteIcon={<ExpandMoreIcon/>}
                        onDelete={(e) => setVersionsMenu({anchor: e.currentTarget.parentElement, row})}
                        onClick={(e) => setVersionsMenu({anchor: e.currentTarget, row})}
                        sx={{height: 22, fontSize: 10, cursor: "pointer"}}
                    />
                );
            },
        },
        {
            field: "routingKey", headerName: t('admin.catalog.col.routingKey'), width: 150,
            valueGetter: (_v, row) => row.rabbitmq?.routingKey || row.id,
            renderCell: ({value}) => (
                <Typography
                    variant="caption"
                    noWrap
                    title={value}
                    sx={{
                        fontFamily: "monospace",
                        fontSize: 11
                    }}>{value}</Typography>
            ),
        },
        {
            field: "sourceMode", headerName: t('admin.catalog.col.sourceMode'), width: 90,
            align: "center", headerAlign: "center",
            valueGetter: (_v, row) => row.image?.sourceMode || "BUILD",
            renderCell: ({value}) => {
                const color = value === "BUILD" ? "info" : value === "NONE" ? "error" : "default";
                return <Chip label={t(`analytics.catalog.sourceMode.${value.toLowerCase()}`)} size="small" variant="outlined" color={color} sx={{height: 20, fontSize: 10, fontWeight: 600}}/>;
            },
        },
        {
            field: "buildStatus", headerName: t('admin.catalog.col.build'), width: 110, sortable: false,
            align: "center", headerAlign: "center",
            renderCell: ({row}) => {
                if (buildStsLoading && !buildStatuses[row.id])
                    return <CircularProgress size={12} sx={{color: "text.disabled"}}/>;
                const s = extractStatus(buildStatuses[row.id]);
                if (!s) return <Chip label="—" size="small" variant="outlined" color="default"/>;
                return (
                    <Chip label={s} size="small" color={buildStatusColor(s)} variant="filled"
                          sx={{height: 20, fontSize: 10, fontWeight: 700}}/>
                );
            },
        },
        {
            field: "image", headerName: t('admin.catalog.col.dockerImage'), flex: 2, minWidth: 280,
            renderCell: ({row}) => {
                const img = row.image;
                const str = typeof img === "object" && img
                    ? `${img.registry || ""}/${img.image || ""}:${img.tag || ""}`.replace(/^\//, "").replace(/:$/, "")
                    : (typeof img === "string" ? img : "—");
                return (
                    <Typography
                        variant="caption"
                        noWrap
                        title={str}
                        sx={{
                            fontFamily: "monospace",
                            fontSize: 11
                        }}>{str}</Typography>
                );
            },
        },
        {
            field: "actions", headerName: t('admin.catalog.col.actions'), width: 200, sortable: false, filterable: false,
            align: "center", headerAlign: "center",
            renderCell: ({row}) => {
                const deployed = deployStatuses[row.id]?.replicas > 0;
                return (
                    <Stack
                        direction="row"
                        sx={{
                            gap: 0.5,
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "100%"
                        }}>
                        <Tooltip title={t('admin.catalog.tooltip.edit')}>
                            <IconButton size="small" sx={{color: "warning.main"}} onClick={() => setEditTarget(row)}>
                                <EditIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={row.image?.sourceMode === "NONE" ? t('admin.catalog.tooltip.sourceModeNone') : row.image?.sourceMode === "PULL" ? t('admin.catalog.tooltip.sourceModePull') : t('admin.catalog.tooltip.rebuild')}>
                            <span>
                            <IconButton size="small" sx={{color: "info.main"}} onClick={() => handleRebuild(row)}
                                        disabled={building === row.id || row.image?.sourceMode === "PULL" || row.image?.sourceMode === "NONE"}>
                                {building === row.id ? <CircularProgress size={14}/> : <BuildIcon fontSize="small"/>}
                            </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t('admin.catalog.tooltip.clone')}>
                            <IconButton size="small" color="primary" onClick={() => handleClone(row)}>
                                <FileCopyIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('admin.catalog.tooltip.copyJson')}>
                            <IconButton size="small" onClick={() => handleCopyJson(row)}>
                                <ContentCopyIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={deployed ? t('admin.catalog.tooltip.deleteDeployed') : t('admin.catalog.tooltip.delete')}>
                            <span>
                                <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}
                                            disabled={deployed}>
                                    <DeleteIcon fontSize="small"/>
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t('admin.catalog.tooltip.buildLogs')}>
                            <IconButton size="small" color="info" onClick={() => setBuildLogsModelId(row.id)}>
                                <ArticleIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );
            },
        },
    ];

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: "auto" }}>
            {/* Header */}
            <Stack
                direction="row"
                sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 3
                }}>
                <Stack
                    direction="row"
                    sx={{
                        alignItems: "center",
                        gap: 1.5
                    }}>
                    <CollectionsBookmarkIcon color="primary" sx={{fontSize: 34}}/>
                    <Box>
                        <Typography variant="h5" sx={{
                            fontWeight: 700
                        }}>{t('admin.catalog.title')}</Typography>
                        <Typography variant="body2" sx={{
                            color: "text.secondary"
                        }}>{t('admin.catalog.subtitle')}</Typography>
                    </Box>
                </Stack>

                <Stack
                    direction="row"
                    sx={{
                        gap: 0.5,
                        alignItems: "center"
                    }}>
                    <Tooltip title={t('admin.catalog.tooltip.copyAll')}>
                        <IconButton onClick={async () => {
                            try {
                                await navigator.clipboard.writeText(JSON.stringify(models, null, 2));
                                showToast({msg: t('admin.catalog.toast.copiedToClipboard', {count: models.length, s: models.length > 1 ? "s" : ""}), severity: "success"});
                            } catch {
                                showToast({msg: t('admin.catalog.toast.copyError'), severity: "error"});
                            }
                        }}>
                            <ContentCopyIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('admin.catalog.tooltip.bulkImport')}>
                        <IconButton onClick={handleBulkOpen}>
                            <UploadFileIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('admin.catalog.tooltip.newModel')}>
                        <IconButton color="primary" onClick={() => setEditTarget("new")}>
                            <AddIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <RefreshControls
                        onRefresh={load}
                        refreshing={loading}
                        autoRefresh={autoRefresh}
                        onAutoRefreshChange={setAutoRefresh}
                        intervalMs={intervalMs}
                        onIntervalChange={setIntervalMs}
                    />
                </Stack>
            </Stack>
            {/* DataGrid */}
            <Paper elevation={1} sx={{borderRadius: 2, overflow: "hidden"}}>
                <DataGrid
                    rows={groupedModels}
                    columns={columns}
                    getRowId={r => r.id}
                    rowHeight={50}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                        pagination: {paginationModel: {pageSize: 25}},
                        sorting: {sortModel: [{field: "groupId", sort: "asc"}]},
                    }}
                    getRowClassName={(params) => params.row.groupId ? `row-group-${params.row.groupId}` : ""}
                    disableRowSelectionOnClick
                    disableColumnResize={false}
                    loading={loading}
                    sx={{
                        ...dgStyle(),
                        minHeight: 400,
                        ...Object.fromEntries(
                            [...new Set(models.map(m => m.groupId).filter(Boolean))].map((gid) => {
                                const gc = groupColor(gid);
                                return [`& .row-group-${gid}`, {
                                    backgroundColor: alpha(gc, 0.04),
                                    borderLeft: `3px solid ${alpha(gc, 0.6)}`,
                                    "&:hover": { backgroundColor: alpha(gc, 0.10) },
                                    "&.Mui-selected": { backgroundColor: alpha(gc, 0.16) },
                                }];
                            })
                        ),
                    }}
                />
            </Paper>
            {/* Popover des versions d'un modele groupe */}
            <Popover
                open={!!versionsMenu.anchor}
                anchorEl={versionsMenu.anchor}
                onClose={() => setVersionsMenu({anchor: null, row: null})}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                transformOrigin={{vertical: "top", horizontal: "left"}}
            >
                <Box sx={{p: 1, minWidth: 320}}>
                    <Typography variant="overline" sx={{px: 1, color: "text.secondary"}}>
                        {versionsMenu.row?.id} — {versionsMenu.row?._versions?.length || 0} version(s)
                    </Typography>
                    <List dense disablePadding>
                        {(versionsMenu.row?._versions || []).map((ver, i) => {
                            const v = ver.version || ver.image?.tag || "?";
                            const isLatest = i === 0;
                            const buildingThis = building === `${ver.id}@${ver.version || ""}`;
                            return (
                                <ListItem
                                    key={v}
                                    secondaryAction={
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title={t('admin.catalog.tooltip.edit')}>
                                                <IconButton size="small" sx={{color: "warning.main"}}
                                                            onClick={() => { setEditTarget(ver); setVersionsMenu({anchor: null, row: null}); }}>
                                                    <EditIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('admin.catalog.tooltip.rebuild')}>
                                                <span>
                                                    <IconButton size="small" sx={{color: "info.main"}}
                                                                disabled={buildingThis || ver.image?.sourceMode === "PULL" || ver.image?.sourceMode === "NONE"}
                                                                onClick={() => handleRebuild(ver)}>
                                                        {buildingThis ? <CircularProgress size={14}/> : <BuildIcon fontSize="small"/>}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={t('admin.catalog.tooltip.delete')}>
                                                <IconButton size="small" color="error"
                                                            onClick={() => handleDeleteVersion(ver.id, ver.version)}>
                                                    <DeleteIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    }
                                >
                                    <ListItemText
                                        primary={<span style={{fontFamily: "monospace"}}>v{v}{isLatest && <Chip label="latest" size="small" color="primary" variant="outlined" sx={{ml: 1, height: 16, fontSize: 9}}/>}</span>}
                                        secondary={ver.image?.tag ? `tag: ${ver.image.tag}` : undefined}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
            </Popover>
            {/* Dialog création / édition */}
            <ModelEditDialog
                target={editTarget}
                groups={groups}
                existingIds={models.map(m => m.id)}
                onClose={() => setEditTarget(null)}
                onSaved={load}
                showToast={showToast}
            />
            {/* Dialog logs de build */}
            <BuildLogsViewer
                open={!!buildLogsModelId}
                modelId={buildLogsModelId}
                onClose={() => setBuildLogsModelId(null)}
                showToast={showToast}
            />
            {/* Dialog bulk import */}
            <Dialog open={bulkOpen} onClose={() => !bulkImporting && setBulkOpen(false)}
                    maxWidth="md" fullWidth>
                <DialogTitle sx={{fontWeight: 700}}>
                    {t('admin.catalog.bulk.dialogTitle')}
                </DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "text.secondary",
                            mb: 2
                        }}>
                        {t('admin.catalog.bulk.description')} <code>id</code>.
                    </Typography>
                    <TextField
                        multiline
                        fullWidth
                        minRows={12}
                        maxRows={24}
                        placeholder={'[\n  { "id": "model-1", "label": "Mon modele", ... },\n  { "id": "model-2", ... }\n]'}
                        value={bulkJson}
                        onChange={(e) => { setBulkJson(e.target.value); setBulkError(null); }}
                        error={!!bulkError}
                        helperText={bulkError}
                        disabled={bulkImporting}
                        slotProps={{input: {sx: {fontFamily: "monospace", fontSize: 12}}}}
                    />

                    {bulkImporting && (
                        <Box sx={{mt: 2}}>
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: "space-between",
                                    mb: 0.5
                                }}>
                                <Typography variant="caption" sx={{
                                    color: "text.secondary"
                                }}>
                                    {t('admin.catalog.bulk.progress', {done: bulkProgress.done, total: bulkProgress.total})}
                                </Typography>
                                {bulkProgress.errors.length > 0 && (
                                    <Typography variant="caption" color="error">
                                        {t('admin.catalog.bulk.errorCount', {count: bulkProgress.errors.length, s: bulkProgress.errors.length > 1 ? "s" : ""})}
                                    </Typography>
                                )}
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0}
                            />
                            {bulkProgress.errors.length > 0 && (
                                <Typography variant="caption" color="error" sx={{mt: 0.5, display: "block"}}>
                                    {t('admin.catalog.bulk.failures', {list: bulkProgress.errors.join(", ")})}
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <Divider/>
                <DialogActions sx={{px: 3, py: 1.5}}>
                    {bulkImporting ? (
                        <Button color="error" onClick={() => { bulkCancelRef.current = true; }}>
                            {t('admin.catalog.bulk.stop')}
                        </Button>
                    ) : (
                        <Button onClick={() => setBulkOpen(false)}>
                            {t('admin.catalog.bulk.close')}
                        </Button>
                    )}
                    <Button variant="contained" onClick={handleBulkImport}
                            disabled={bulkImporting || !bulkJson.trim()}
                            startIcon={bulkImporting ? <CircularProgress size={16}/> : <UploadFileIcon/>}>
                        {bulkImporting ? t('admin.catalog.bulk.importing') : t('admin.catalog.bulk.import')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
